import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { ChevronLeft, ChevronRight, Sparkles } from "lucide-react";
import { http } from "@/lib/api";

/**
 * WorkshopsCarousel — inline carousel of the 4 recurring workshops.
 *
 * Cover images are pulled at runtime from `/api/events` so they always match
 * whatever the admin has uploaded most recently — no hardcoded URLs to rot.
 * Falls back to the static list below if the API is unreachable.
 */
const WORKSHOPS = [
    { key: "Harmonisation", series: "harmonisation", title: "Harmonisation de l'être", price: "12 €", rhythm: "Chaque lundi", subscription: "Tisseur" },
    { key: "Éveil", series: "eveil", title: "Éveil de conscience", price: "22 €", rhythm: "Chaque jeudi", subscription: "Messager" },
    { key: "Libération", series: "liberation", title: "Libération de l'Être", price: "44 €", rhythm: "Un dimanche sur deux", subscription: "Alchimiste" },
    { key: "Unification", series: "unification", title: "Unification de l'âme", price: "55 €", rhythm: "Une fois par mois", subscription: null },
];

export default function WorkshopsCarousel() {
    const [emblaRef, embla] = useEmblaCarousel({ loop: true, align: "start" }, [Autoplay({ delay: 5500, stopOnInteraction: true })]);
    const [covers, setCovers] = useState({});

    useEffect(() => {
        // Robust cover lookup: /public/workshop-covers matches by `series`
        // and scans past + future events → the cover keeps showing even when
        // no upcoming session is scheduled yet. Falls back silently if the
        // endpoint is unreachable (placeholder Sparkles icon).
        http.get("/public/workshop-covers").then(({ data }) => {
            const map = {};
            for (const w of WORKSHOPS) {
                const c = data?.[w.series];
                if (c?.image_url) map[w.key] = c.image_url;
            }
            setCovers(map);
        }).catch(() => {});
    }, []);

    return (
        <div className="my-10 -mx-2" data-testid="workshops-carousel">
            <div className="text-xs tracking-[0.25em] uppercase text-primary font-semibold mb-4 px-2 flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5" /> Les 4 ateliers en un coup d'œil
            </div>
            <div className="relative">
                <div ref={emblaRef} className="overflow-hidden rounded-2xl">
                    <div className="flex">
                        {WORKSHOPS.map((w) => (
                            <div key={w.key} className="min-w-0 flex-[0_0_85%] sm:flex-[0_0_50%] lg:flex-[0_0_33.333%] px-2">
                                <Link to="/evenements" className="block group">
                                    <div className="rounded-2xl overflow-hidden border border-border/60 bg-card hover:border-primary/40 transition-colors">
                                        <div className="aspect-[4/3] bg-muted overflow-hidden">
                                            {covers[w.key] ? (
                                                <img
                                                    src={covers[w.key]}
                                                    alt={`Atelier ${w.title} — Foundation of Light`}
                                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                                    loading="lazy"
                                                />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center">
                                                    <Sparkles className="w-8 h-8 text-primary/40" />
                                                </div>
                                            )}
                                        </div>
                                        <div className="p-4">
                                            <h3 className="font-serif text-lg mb-1 group-hover:text-primary transition-colors">{w.title}</h3>
                                            <div className="text-xs text-muted-foreground space-y-0.5">
                                                <div>{w.rhythm} · <strong className="text-foreground">{w.price}</strong></div>
                                                {w.subscription && <div>Inclus dans l'abo <strong>{w.subscription}</strong></div>}
                                            </div>
                                        </div>
                                    </div>
                                </Link>
                            </div>
                        ))}
                    </div>
                </div>
                <button
                    type="button"
                    onClick={() => embla && embla.scrollPrev()}
                    className="hidden sm:flex absolute -left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-background border border-border/60 items-center justify-center shadow hover:bg-muted"
                    aria-label="Précédent"
                    data-testid="workshops-carousel-prev"
                >
                    <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                    type="button"
                    onClick={() => embla && embla.scrollNext()}
                    className="hidden sm:flex absolute -right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-background border border-border/60 items-center justify-center shadow hover:bg-muted"
                    aria-label="Suivant"
                    data-testid="workshops-carousel-next"
                >
                    <ChevronRight className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
}
